<template>
  <section class="stats-section">
    <div class="stats-container">
      <div class="stats-header">
        <h2 class="stats-title">{{ t('stats.heading') }}</h2>
        <p class="stats-subtitle">{{ t('stats.subheading') }}</p>
      </div>
      <div class="stats-grid">
        <div class="stat-card">
          <div class="stat-value">{{ animatedSatisfaction }}%</div>
          <div class="stat-label">{{ t('stats.satisfaction_rate') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ animatedTrainees }}</div>
          <div class="stat-label">{{ t('stats.registered_trainees') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ animatedTrainers }}+</div>
          <div class="stat-label">{{ t('stats.online_trainers') }}</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ animatedSessions }}</div>
          <div class="stat-label">{{ t('stats.completed_sessions') }}</div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useLanguage } from '@/composables/useLanguage'

const { t } = useLanguage()

const animatedSatisfaction = ref('0');
const animatedTrainees = ref('0');
const animatedTrainers = ref('0');
const animatedSessions = ref('0');

const animateValue = (start, end, duration, callback, isDecimal = false) => {
  const range = end - start;
  const increment = range / (duration / 16);
  let current = start;

  const timer = setInterval(() => {
    current += increment;
    if (current >= end) {
      current = end;
      clearInterval(timer);
    }
    if (isDecimal) {
      callback(current.toFixed(1));
    } else {
      callback(Math.floor(current).toLocaleString('en-US'));
    }
  }, 16);
};

onMounted(() => {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Start animations
          animateValue(0, 98.4, 2000, (val) => {
            animatedSatisfaction.value = val;
          }, true);

          animateValue(0, 22850, 2000, (val) => {
            animatedTrainees.value = val;
          });

          animateValue(0, 240, 2000, (val) => {
            animatedTrainers.value = val;
          });

          animateValue(0, 2441506, 2000, (val) => {
            animatedSessions.value = val;
          });

          observer.disconnect();
        }
      });
    },
    { threshold: 0.3 }
  );

  const section = document.querySelector('.stats-section');
  if (section) {
    observer.observe(section);
  }
});
</script>

<style scoped>
/* Main Section */
.stats-section {
  width: 100%;
  background-color: #fff;
  padding: clamp(40px, 8vw, 80px) clamp(20px, 5vw, 100px);
  overflow-x: hidden;
}

/* Container */
.stats-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: clamp(40px, 6vw, 60px);
}

/* Header */
.stats-header {
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
}

.stats-title {
  margin: 0 0 clamp(12px, 2vw, 20px) 0;
  font-size: clamp(24px, 4vw, 40px);
  font-weight: 700;
  line-height: 1.2;
  color: #165e84;
  text-transform: uppercase;
}

.stats-subtitle {
  margin: 0;
  font-size: clamp(14px, 1.8vw, 18px);
  line-height: 1.5;
  color: #808080;
  opacity: 0.9;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: clamp(20px, 3vw, 30px);
  width: 100%;
}

/* Stat Cards */
.stat-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px;
  padding: clamp(24px, 3vw, 36px) clamp(16px, 2vw, 24px);
  text-align: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: clamp(12px, 1.5vw, 16px);
  min-height: clamp(140px, 18vw, 180px);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.stat-value {
  font-size: clamp(32px, 5vw, 48px);
  font-weight: 700;
  color: #165e84;
  line-height: 1;
  text-transform: uppercase;
}

.stat-label {
  font-size: clamp(14px, 1.8vw, 18px);
  font-weight: 500;
  color: #808080;
  line-height: 1.3;
}

/* RTL Support */
html[lang="ar"] .stats-section {
  font-family: 'Cairo', sans-serif;
  direction: rtl;
}

html[lang="ar"] .stats-title {
  font-size: clamp(26px, 4.5vw, 44px);
}

html[lang="ar"] .stat-value {
  font-family: 'Poppins', sans-serif; /* Numbers stay in English font */
}

/* Tablet */
@media (max-width: 768px) {
  .stats-section {
    padding: clamp(30px, 6vw, 50px) clamp(15px, 4vw, 30px);
  }

  .stats-container {
    gap: clamp(30px, 5vw, 40px);
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: clamp(15px, 2.5vw, 20px);
  }

  .stat-card {
    padding: clamp(20px, 3vw, 28px) clamp(12px, 2vw, 20px);
    min-height: clamp(120px, 16vw, 150px);
  }
}

/* Mobile */
@media (max-width: 480px) {
  .stats-section {
    padding: clamp(24px, 5vw, 36px) clamp(12px, 3vw, 20px);
  }

  .stats-container {
    gap: clamp(24px, 4vw, 32px);
  }

  .stats-title {
    font-size: clamp(20px, 5vw, 28px);
  }

  .stats-subtitle {
    font-size: clamp(13px, 3vw, 15px);
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: clamp(12px, 2vw, 16px);
  }

  .stat-card {
    padding: clamp(16px, 2.5vw, 24px) clamp(10px, 1.5vw, 16px);
    min-height: clamp(100px, 20vw, 130px);
    gap: clamp(8px, 1.5vw, 12px);
  }

  .stat-value {
    font-size: clamp(24px, 6vw, 36px);
  }

  .stat-label {
    font-size: clamp(12px, 2.5vw, 14px);
  }
}

/* Very small mobile */
@media (max-width: 360px) {
  .stats-section {
    padding: 20px 10px;
  }

  .stat-card {
    padding: 14px 8px;
    min-height: 90px;
  }

  .stat-value {
    font-size: 22px;
  }

  .stat-label {
    font-size: 11px;
  }
}
</style>
